import { useState } from 'react';
import { CheckCircle2, Send, Undo2, UnlockKeyhole, UserCog } from 'lucide-react';
import { Button, Card, toast } from '../../components/ui';
import { cn } from '../../lib/cn';
import {
  useTimesheetAction,
  type RunningTimer,
  type Timesheet,
  type TimesheetRange,
  type WeekData,
} from './api';
import { weekCompleteness } from './completeness';
import { formatHoursUnit, todayIso, type HoursFormat } from './hours';
import {
  TimesheetNote,
  TimesheetStatusPill,
  daysGovernedBy,
  periodTarget,
  periodsInView,
  shortIsoDate,
  submitLabel,
  timesheetProvenance,
} from './timesheet-status';
import { TimesheetActionDialog, type DialogAction } from './TimesheetActionDialog';

/**
 * The timesheet header's submission strip — the "am I done?" action.
 *
 * ⚠ A TIMESHEET IS A PERIOD, NOT ALWAYS THE WEEK ON SCREEN. A monthly
 * contractor submits a MONTH, a daily one a DAY, and the screen still views a
 * week — so the strip describes every period the viewed days belong to, one
 * line each: "August 2026 · Approved" above "September 2026 · Draft · submit
 * from Sep 30" on the week of Aug 31. The Submit button names what it sends
 * ("Submit August 2026"), because "Submit week" over a month is a lie.
 *
 * The state colour is carried by a left rule rather than a filled banner: this
 * strip is permanent furniture on the busiest screen in the app, and a
 * full-width tinted block every week would train people to look past it.
 */

const TONE: Record<Timesheet['status'], string> = {
  DRAFT: 'border-l-grey-300',
  SUBMITTED: 'border-l-status-open',
  APPROVED: 'border-l-success',
  REJECTED: 'border-l-error',
};

const KIND_WORD = { DAY: 'day', WEEK: 'week', MONTH: 'month' } as const;

export interface WeekApprovalBarProps {
  /** The submission state of the viewed range (rides on /time/week and /time/day). */
  range: TimesheetRange;
  /** Hours per viewed day — what each period holds ON SCREEN. */
  dayTotals: Record<string, number>;
  /** Whose sheet this is (explicit, so an admin acting for someone is honest). */
  personId: string;
  /**
   * The week, when one is on screen — so a WEEK submission can say what is
   * missing at the moment of commitment. Not passed on to a month or a day:
   * "this week looks unfinished" over a monthly submission is the wrong fact.
   */
  week?: WeekData;
  /** Only describe the period governing this day (the day view). */
  focusDate?: string;
  /** The server's word on who may correct a submitted period (admins). */
  isAdmin?: boolean;
  /** The unit the grid below is currently showing — this bar must match it. */
  hoursFormat: HoursFormat;
  /**
   * The clock, if one is running. Submission is refused while a timer sits in
   * the period, so the dialog has to be able to say so — and to stop it in the
   * same press.
   */
  runningTimer?: RunningTimer | null;
}

function anyAbility(sheet: Timesheet): boolean {
  const { can } = sheet;
  return can.submit || can.unsubmit || can.approve || can.reject || can.reopen;
}

export function WeekApprovalBar({
  range,
  dayTotals,
  personId,
  week,
  focusDate,
  isAdmin = false,
  hoursFormat,
  runningTimer = null,
}: WeekApprovalBarProps) {
  const [dialog, setDialog] = useState<{ action: DialogAction; sheet: Timesheet } | null>(null);
  const unsubmit = useTimesheetAction('unsubmit');
  const today = todayIso();

  let rows = periodsInView(range, focusDate);
  // A DAILY person's week is seven periods. Seven lines of "Draft" is chrome,
  // not information: keep the days that have a state or an action, and let
  // the rest be implied.
  if (rows.length > 2 && rows.every((r) => r.periodKind === 'DAY')) {
    const notable = rows.filter((r) => r.status !== 'DRAFT' || anyAbility(r));
    rows = notable.length > 0 ? notable : [rows.find((r) => r.periodStart === today) ?? rows[0]];
  }

  const ownerName = range.timesheet.person.name;
  const isOwn = range.timesheet.isOwn;

  /**
   * Hours of a period that are on screen, and whether that is all of it.
   *
   * ⚠ `whole` is decided on every day of the viewed range the period governs,
   * THEN the figure narrows. In the Day view a period that is only partly on
   * screen (a month) contributes the FOCUSED day's hours and nothing else —
   * summing the week's September days and labelling them "this day" printed
   * 32 h over a day that held 6. A whole period (a day, a week) keeps its
   * whole figure beside its own label ("Week of Sep 14 · 32.00 h tracked").
   */
  const onScreen = (sheet: Timesheet) => {
    const governed = daysGovernedBy(range, sheet);
    const whole =
      governed[0] === sheet.periodStart && governed[governed.length - 1] === sheet.periodEnd;
    const days = !whole && focusDate ? governed.filter((d) => d === focusDate) : governed;
    const hours = days.reduce((sum, d) => sum + (dayTotals[d] ?? 0), 0);
    return { hours: Math.round(hours * 100) / 100, whole };
  };

  // Nobody expects a sheet from this person and nothing is recorded: one quiet
  // label, no pill, no button. "Draft" would imply a submission is owed.
  const quiet = rows.every((r) => !r.expected && r.status === 'DRAFT' && !anyAbility(r));
  if (quiet) {
    // This line names no period, so its figure is what the SCREEN is about:
    // the focused day in the Day view, the viewed week otherwise. A week's
    // hours under a single day read as that day's.
    const total = focusDate
      ? Math.round((dayTotals[focusDate] ?? 0) * 100) / 100
      : rows.reduce((sum, r) => sum + onScreen(r).hours, 0);
    return (
      <Card className="mb-3 border-l-4 border-l-grey-200">
        <p className="px-3 py-2 text-body-sm text-grey-500 sm:px-4">
          {!isOwn && <span className="font-bold text-secondary">{ownerName} · </span>}
          {total > 0
            ? `${formatHoursUnit(total, hoursFormat)} ${focusDate ? 'this day' : 'tracked'} · `
            : ''}
          not expected to submit
        </p>
      </Card>
    );
  }

  /** Withdrawing your own submission is instant — see TimesheetActionDialog. */
  const withdraw = (sheet: Timesheet) => {
    unsubmit.mutate(periodTarget(sheet, personId), {
      onSuccess: () =>
        toast.success(`Submission withdrawn — ${sheet.label} is editable again.`),
      onError: (error) =>
        toast.error(error instanceof Error ? error.message : 'Could not withdraw the submission.'),
    });
  };

  const dialogSheet = dialog?.sheet ?? null;
  const dialogScreen = dialogSheet ? onScreen(dialogSheet) : null;
  const dialogIsThisWeek =
    Boolean(week && dialogSheet) &&
    dialogSheet!.periodKind === 'WEEK' &&
    dialogSheet!.periodStart === week!.weekStart;

  return (
    <>
      <Card className="mb-3 overflow-hidden">
        <div className="divide-y divide-grey-100">
          {rows.map((sheet) => {
            const { can, status } = sheet;
            const provenance = timesheetProvenance(sheet);
            const screen = onScreen(sheet);
            const word = KIND_WORD[sheet.periodKind];
            const opensLater =
              sheet.expected &&
              (status === 'DRAFT' || status === 'REJECTED') &&
              !can.submit &&
              sheet.submittableFrom > today;
            const correcting = isAdmin && status === 'SUBMITTED';
            /**
             * DRAFT is the only state with no history, so it says what the
             * period HOLDS instead. A month is only partly on screen, so its
             * hours are "this week's" (or, in the Day view, the focused day's
             * alone — `onScreen` narrows them), never passed off as the month's.
             */
            const holds =
              screen.hours > 0
                ? `${formatHoursUnit(screen.hours, hoursFormat)} ${
                    screen.whole ? 'tracked' : focusDate ? 'this day' : 'this week'
                  }`
                : screen.whole
                  ? 'Nothing tracked yet'
                  : null;
            const line = [
              provenance ??
                [holds, sheet.expected ? 'not submitted yet' : 'not expected to submit']
                  .filter(Boolean)
                  .join(' · '),
              opensLater ? `submit from ${shortIsoDate(sheet.submittableFrom)}` : null,
            ]
              .filter(Boolean)
              .join(' · ');
            const ReviewGlyph = status === 'APPROVED' ? CheckCircle2 : Send;

            return (
              <div key={`${sheet.periodKind}:${sheet.periodStart}`} className={cn('border-l-4', TONE[status])}>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-3 py-2.5 sm:px-4">
                  <TimesheetStatusPill status={status} />
                  <div className="min-w-0 basis-full sm:flex-1 sm:basis-auto">
                    <p className="text-body-sm leading-snug text-grey-600">
                      {!isOwn && <span className="font-bold text-secondary">{ownerName} · </span>}
                      <span className="font-bold text-secondary">{sheet.label}</span>
                      {line && <> · {line}</>}
                    </p>
                    {sheet.locked && (
                      <p className="mt-0.5 flex items-center gap-1.5 text-caption text-grey-500">
                        {correcting ? (
                          <>
                            {/* The reviewing glyph from authorship.tsx — an
                                admin writing into someone's submitted period
                                is the same act, with the same trail. */}
                            <UserCog className="h-3 w-3 shrink-0 text-accent" aria-hidden />
                            {isOwn
                              ? 'You can still correct these hours as an admin.'
                              : `You can correct these hours as an admin — every change is signed and shown to ${ownerName}.`}
                          </>
                        ) : (
                          <>
                            {/* The review lock wears the period's own status
                                glyph; `Lock` belongs to the BILLING lock. */}
                            <ReviewGlyph className="h-3 w-3 shrink-0" aria-hidden />
                            {status === 'APPROVED'
                              ? `Read-only until the ${word} is reopened.`
                              : `Read-only while the ${word} waits for review.`}
                          </>
                        )}
                      </p>
                    )}
                  </div>

                  <div className="flex flex-wrap items-center gap-2 max-sm:w-full max-sm:justify-end">
                    {can.unsubmit && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => withdraw(sheet)}
                        loading={unsubmit.isPending}
                        title={`Take ${sheet.label} back so you can edit it`}
                      >
                        <Undo2 className="h-4 w-4" aria-hidden />
                        Withdraw
                      </Button>
                    )}
                    {can.reject && (
                      <Button variant="outline" size="sm" onClick={() => setDialog({ action: 'reject', sheet })}>
                        Send back
                      </Button>
                    )}
                    {can.approve && (
                      <Button size="sm" onClick={() => setDialog({ action: 'approve', sheet })}>
                        Approve {word}
                      </Button>
                    )}
                    {can.reopen && (
                      <Button variant="outline" size="sm" onClick={() => setDialog({ action: 'reopen', sheet })}>
                        <UnlockKeyhole className="h-4 w-4" aria-hidden />
                        Reopen
                      </Button>
                    )}
                    {can.submit ? (
                      <Button size="sm" onClick={() => setDialog({ action: 'submit', sheet })}>
                        <Send className="h-4 w-4" aria-hidden />
                        {submitLabel(sheet)}
                      </Button>
                    ) : (
                      // Not open yet: the action is shown, off, with the day it
                      // opens — never hidden, which reads as a missing feature.
                      opensLater &&
                      isOwn && (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled
                          aria-label={`${submitLabel(sheet)} — opens ${shortIsoDate(sheet.submittableFrom)}`}
                        >
                          <Send className="h-4 w-4" aria-hidden />
                          {submitLabel(sheet)}
                        </Button>
                      )
                    )}
                  </div>
                </div>

                {/* The reviewer's words, given their own line and a red rule when
                    the period was sent back — the one thing its owner must read. */}
                {sheet.reviewNote && (
                  <div className="px-3 pb-2.5 sm:px-4">
                    <TimesheetNote
                      note={sheet.reviewNote}
                      author={sheet.reviewedBy?.name ?? 'Approver'}
                      tone={status === 'REJECTED' ? 'warning' : 'neutral'}
                    />
                  </div>
                )}
                {/* The submitter's own note stays visible to the approver. */}
                {sheet.note && status !== 'DRAFT' && (
                  <div className="px-3 pb-2.5 sm:px-4">
                    <TimesheetNote note={sheet.note} author={ownerName} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Card>

      <TimesheetActionDialog
        action={dialog?.action ?? null}
        period={dialogSheet}
        personId={personId}
        personName={ownerName}
        // A figure only when the whole period is on screen — a month's hours
        // are not this week's, and the dialog must not print either as both.
        hours={dialogScreen?.whole ? dialogScreen.hours : undefined}
        empty={Boolean(dialogScreen?.whole && dialogScreen.hours <= 0)}
        completeness={dialogIsThisWeek && week ? weekCompleteness(week) : undefined}
        hoursFormat={hoursFormat}
        runningTimer={runningTimer}
        onClose={() => setDialog(null)}
      />
    </>
  );
}
